import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import { IFlexGridWrapperContext } from 'core-components/sitebuilder/layout/context/FlexGridWrapperContext'
import { createSitePage } from 'core-components/sitebuilder/layout/helpers/sitePages'
import { IFlexLayout } from 'core-components/sitebuilder/models/flexGrid/IFlexLayout'
import { ISitePage } from 'core-components/sitebuilder/models/ISitePage'
import { IWidget } from 'core-components/sitebuilder/models/IWidget'
import { IWidgetsData } from 'core-components/sitebuilder/models/IWidgetData'
import MeasureController from 'src/core/analytics/MeasureController'
import { StorageApi } from 'src/core/classes/services/api/StorageApi'
import { KEY_SITEPAGES_LAYOUT_DATA } from 'src/core/components/app/loaders/constants/sitePage'
import { useUserStore } from 'src/core/hooks/services/useUserStore'
import { useSitePagesService } from 'src/core/sitebuilder/hooks'
import { useLovSource } from 'src/core/sitebuilder/widgets/lovDropdowns'
import { createGroup } from 'src/core/sitebuilder/widgets/lovDropdowns/helpers/getGroupLovSource'
import { GROUP_NAME_LOV_KEY } from 'src/core/sitebuilder/widgets/sitePagesDetails/constants'
import { saveSitePageToCache } from 'src/core/store/service/sitebuilder/endpoints/pageApi/helpers/saveSitePageToCache'
import {
  pageApi,
  useUpdateSitePageMutation,
  useUpdateUserPageLayoutMutation,
} from 'src/core/store/service/sitebuilder/pageApi'
import {
  useAddWidgetLocalMutation,
  useSaveWidgetMutation,
} from 'src/core/store/service/sitebuilder/widgetApi'
import Logger from 'src/core/utils/Logger'

export const useFlexGridWrapperContextValues = () => {
  const isAdmin = useUserStore().isAdminUser()
  const isGuest = useUserStore().isGuestUser()
  const [saveWidget] = useSaveWidgetMutation()
  const [updateSitePageApi] = useUpdateSitePageMutation()
  const { updatePageWidgetsData } = useSitePagesService()
  const dispatch = useDispatch()

  const { LOVSource } = useLovSource({
    key: 'SitePageDetails_GroupName',
    root: GROUP_NAME_LOV_KEY,
  })

  const saveAsDefaultHandler = useCallback(
    async (sitePage: ISitePage) => {
      await updateSitePageApi({ sitePage })
    },
    [updateSitePageApi]
  )

  const getDefaultSettings = useCallback(
    async (pageId: string) => {
      const result = await dispatch(pageApi.endpoints.fetchSitePage.initiate(pageId))
      if ('data' in result && result.data) {
        return result.data as ISitePage
      }
      return createSitePage()
    },
    [dispatch]
  )

  const flexSettingsChanged = useCallback((flexSettings: IFlexLayout) => {
    StorageApi.set('flexSettingsV2', flexSettings).catch(Logger.error)
  }, [])

  const saveWidgetHandler = useCallback(
    async (widget: IWidget) => {
      const result = await saveWidget(widget)
      if ('data' in result) {
        return result.data
      }
      return undefined
    },
    [saveWidget]
  )

  const saveGroup = useCallback(async (groupName: string) => {
    try {
      await createGroup('SitePageDetails_GroupName', groupName, 'CORE: Site Pages group name')
      return true
    } catch {
      return false
    }
  }, [])

  const fetchWidgetGroupsHandler = useCallback(async () => {
    const lovData = await LOVSource.fetch()
    return lovData.items.map((el) => el.Name)
  }, [LOVSource])

  const [addLocalWidget] = useAddWidgetLocalMutation()
  const addWidgetToFlexGridHandler = useCallback(
    async (widget: IWidget) => {
      addLocalWidget(widget)
    },
    [addLocalWidget]
  )

  const [updateUserLayout] = useUpdateUserPageLayoutMutation()
  const updateUserLayoutHandler = useCallback(
    (layoutCode: string, settings: object) => {
      updateUserLayout({ layoutCode, settings })
    },
    [updateUserLayout]
  )

  const saveAsDefaultLayoutHandler = useCallback(
    async (newCurrentSitePage: ISitePage, newWidgetsData: IWidgetsData[]) => {
      await saveSitePageToCache(newCurrentSitePage)
      await updatePageWidgetsData(newCurrentSitePage, newWidgetsData, true)
      return newCurrentSitePage
    },
    [updatePageWidgetsData]
  )

  const flexGridWrapperContextValues: IFlexGridWrapperContext = useMemo(() => {
    return {
      onSaveWidgetsAsDefault: saveAsDefaultHandler,
      onFlexSettingsChanged: flexSettingsChanged,
      getDefaultPageSettings: getDefaultSettings,
      saveWidget: saveWidgetHandler,
      fetchWidgetGroups: fetchWidgetGroupsHandler,
      saveGroup: saveGroup,
      addWidgetToFlexGrid: addWidgetToFlexGridHandler,
      MeasureController: MeasureController,
      canEditSettings: isAdmin,
      isAnonymous: isGuest,
      updateUserLayout: updateUserLayoutHandler,
      saveAsDefaultLayout: saveAsDefaultLayoutHandler,
    }
  }, [
    addWidgetToFlexGridHandler,
    fetchWidgetGroupsHandler,
    flexSettingsChanged,
    getDefaultSettings,
    isAdmin,
    isGuest,
    saveAsDefaultHandler,
    saveAsDefaultLayoutHandler,
    saveGroup,
    saveWidgetHandler,
    updateUserLayoutHandler,
  ])

  return { flexGridWrapperContextValues }
}
